import React from 'react'
import {
  Alert,
  alpha,
  Box,
  Chip,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import Campaign from '@mui/icons-material/Campaign'
import Call from '@mui/icons-material/Call'
import Email from '@mui/icons-material/Email'
import Notifications from '@mui/icons-material/Notifications'
import SouthWest from '@mui/icons-material/SouthWest'
import NorthEast from '@mui/icons-material/NorthEast'
import BusinessCenter from '@mui/icons-material/BusinessCenter'

const CHANNEL_META = {
  whatsapp: {
    label: 'WhatsApp',
    icon: Campaign,
    color: '#0f9d58',
  },
  call: {
    label: 'Calls',
    icon: Call,
    color: '#1565c0',
  },
  push: {
    label: 'Push',
    icon: Notifications,
    color: '#6a1b9a',
  },
  email: {
    label: 'Email',
    icon: Email,
    color: '#ef6c00',
  },
}

const AFFILIATION_ACCENTS = {
  clinic: {
    border: '#2563eb',
    wash: alpha('#2563eb', 0.08),
  },
  aumhum: {
    border: '#0f9d58',
    wash: alpha('#0f9d58', 0.08),
  },
  others: {
    border: '#7c3aed',
    wash: alpha('#7c3aed', 0.08),
  },
}

const DirectionCard = ({ title, icon: Icon, caption, rows }) => {
  const visibleRows = (rows || []).filter((row) => row?.total > 0)

  return (
    <Paper
      variant="outlined"
      sx={{
        height: '100%',
        borderRadius: 2.5,
        p: 2,
        backgroundColor: alpha('#ffffff', 0.85),
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 0.75 }}>
        <Icon sx={{ color: 'text.secondary', fontSize: 18 }} />
        <Typography variant="subtitle1" fontWeight={700}>
          {title}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {caption}
      </Typography>

      {visibleRows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No tracked activity yet.
        </Typography>
      ) : (
        <Stack spacing={1.25}>
          {visibleRows.map((row) => (
            <Box
              key={row.role}
              sx={{
                p: 1.25,
                borderRadius: 2,
                backgroundColor: alpha('#0f172a', 0.03),
                border: '1px solid',
                borderColor: alpha('#0f172a', 0.06),
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
              >
                <Typography variant="body2" fontWeight={700}>
                  {row.label}
                </Typography>
                <Chip
                  label={`Total ${row.total}`}
                  size="small"
                  sx={{ fontWeight: 700, backgroundColor: alpha('#111827', 0.06) }}
                />
              </Stack>

              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                {Object.entries(CHANNEL_META).map(([channelKey, meta]) => {
                  const IconComponent = meta.icon
                  const value = row?.channels?.[channelKey] || 0
                  return (
                    <Chip
                      key={`${row.role}-${channelKey}`}
                      size="small"
                      variant={value > 0 ? 'filled' : 'outlined'}
                      icon={<IconComponent sx={{ color: value > 0 ? meta.color : 'inherit !important' }} />}
                      label={`${meta.label} ${value}`}
                      sx={{
                        borderColor: alpha(meta.color, 0.35),
                        backgroundColor: value > 0 ? alpha(meta.color, 0.12) : 'transparent',
                        color: value > 0 ? meta.color : 'text.secondary',
                        fontWeight: value > 0 ? 700 : 500,
                      }}
                    />
                  )
                })}
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
    </Paper>
  )
}

function LoadingState() {
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 3,
        p: 2.5,
        background: 'linear-gradient(180deg, rgba(248,250,252,0.96) 0%, rgba(241,245,249,0.92) 100%)',
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <BusinessCenter color="primary" />
        <Box sx={{ flex: 1 }}>
          <Skeleton width={210} height={30} />
          <Skeleton width={320} height={22} />
        </Box>
      </Stack>
      <Grid container spacing={2}>
        {[1, 2, 3].map((item) => (
          <Grid item xs={12} md={4} key={item}>
            <Paper variant="outlined" sx={{ borderRadius: 2.5, p: 2 }}>
              <Skeleton width="55%" height={28} />
              <Skeleton width="30%" height={22} sx={{ mb: 1 }} />
              <Skeleton height={90} />
              <Skeleton height={90} />
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Paper>
  )
}

export default function ClinicBrandCommunicationOverview({ summary, loading = false }) {
  if (loading) {
    return <LoadingState />
  }

  if (!summary) {
    return (
      <Alert severity="info" sx={{ borderRadius: 2.5 }}>
        Communication activity for this clinic brand is not available yet.
      </Alert>
    )
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 3,
        p: { xs: 2, md: 2.5 },
        background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.95) 100%)',
        borderColor: alpha('#0f172a', 0.08),
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        sx={{ mb: 2.25 }}
      >
        <Box>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <BusinessCenter sx={{ color: 'primary.main' }} />
            <Typography variant="h6" fontWeight={800}>
              Communications Overview
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            All-time clinic brand delivery volume grouped by recipient affiliation and actor-traceable initiator.
          </Typography>
        </Box>
        <Chip
          label="All Time"
          color="primary"
          variant="outlined"
          sx={{ fontWeight: 700, borderRadius: 999 }}
        />
      </Stack>

      <Grid container spacing={2}>
        {(summary.sections || []).map((section) => {
          const accent = AFFILIATION_ACCENTS[section.key] || AFFILIATION_ACCENTS.others
          return (
            <Grid item xs={12} md={4} key={section.key}>
              <Paper
                variant="outlined"
                sx={{
                  height: '100%',
                  borderRadius: 2.75,
                  p: 2,
                  borderTop: `4px solid ${accent.border}`,
                  backgroundColor: accent.wash,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Typography variant="subtitle1" fontWeight={800}>
                    {section.label}
                  </Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    <Chip
                      label={`To ${section.deliveredTotal}`}
                      size="small"
                      sx={{ fontWeight: 700, backgroundColor: alpha(accent.border, 0.14) }}
                    />
                    <Chip
                      label={`From ${section.fromTotal}`}
                      size="small"
                      sx={{ fontWeight: 700, backgroundColor: alpha('#111827', 0.08) }}
                    />
                  </Stack>
                </Stack>

                <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                  <Grid item xs={12}>
                    <DirectionCard
                      title="Delivered To"
                      icon={SouthWest}
                      caption="Recipient-side counts across WhatsApp, calls, push, and email."
                      rows={section.deliveredTo}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <DirectionCard
                      title="Sent From"
                      icon={NorthEast}
                      caption="Actor-traceable initiations from support, patients, and practitioners."
                      rows={section.sentFrom}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )
        })}
      </Grid>
    </Paper>
  )
}
